import './index.css'

import React, { useState } from 'react'
import { useDrop } from 'react-dnd'

import mixpanel from '@disco/api/mixpanel'
import { AUDIO_FILE } from '@disco/constants/FileTypes'
import { FILE } from '@disco/constants/ItemTypes'
import {
  canFormatBeOptimised,
  getExtension,
  getFilesFromDraggedItem,
  getFileTypeByExt,
} from '@disco/utils/file-utils'

import { dndConnectRef } from 'utils/dnd-connect-ref'

import FileSelect from 'components/common/file-select'
import { Box, Dialog, Flex, Theme } from 'components/ds'
import PendingStatus from 'components/explorer/track/track-media/status'
import Button from 'components/ui/button'

type Track = Record<string, any>

type TrackUploader = {
  tempItem?: {
    tempId: string
    title?: string
    artist?: string
    album?: string
    progress?: number
  }
  uploadedToS3?: boolean
  filename?: string
}

type Props = {
  track: Track
  onClose: () => void
  onDone: () => void
  onUpload: (abortController: unknown) => void
  existingFormat?: string
  uploadTrackFileToS3: (file: File, uploadPolicyUrl: string) => unknown
  finalizeTrackUpload: (uploadPolicyUrl: string, transcode: boolean) => void
  format?: string
  trackUploader: TrackUploader
  isAdding?: boolean
}

function handleUploadingFiles(props: Props, files: File[]) {
  const [file] = files
  if (!file) {
    return
  }
  const ext = getExtension(file.name)

  if (props.isAdding) {
    if (
      canFormatBeOptimised(ext) ===
      canFormatBeOptimised(props.existingFormat ?? '')
    ) {
      return
    }
    if (canFormatBeOptimised(ext)) {
      mixpanel.sendPureEvent('Add lossless version', { Format: ext })
    }
  } else {
    if (
      canFormatBeOptimised(ext) !== canFormatBeOptimised(props.format ?? '')
    ) {
      return
    }
    const event = canFormatBeOptimised(ext)
      ? 'Replace lossless version'
      : 'Replace optimised version'
    mixpanel.sendPureEvent(event, { Format: ext })
  }

  const abortController = props.uploadTrackFileToS3(
    file,
    props.track.upload_policy_v2_url,
  )
  props.onUpload(abortController)
}

function TempItem({ trackUploader }: { trackUploader: TrackUploader }) {
  const item = trackUploader.tempItem
  if (!item) return null
  const { title, artist, album, progress } = item
  const progressIndicator =
    progress !== undefined && progress < 100 ? (
      <div className="ShortlistTrack_Upload">
        <div
          className="ShortlistTrack_UploadProgress"
          style={{ width: `${100 - progress}%` }}
        />
      </div>
    ) : (
      <PendingStatus message="Processing..." size="small" />
    )

  return (
    <div className="Shortlist_Items">
      <div className="Shortlist_Item" key={item.tempId}>
        <div className="ShortlistTrack is-temp">
          <div className="ShortlistTrack_Artwork" />
          <div className="ShortlistTrack_Details">
            <div className="ShortlistTrack_Name">
              {title}
              {progress !== undefined && progress < 100 ? (
                <span className="ShortlistTrack_NamePercent">
                  {`${progress.toFixed(0)}%`}
                </span>
              ) : null}
            </div>
            <div className="ShortlistTrack_ArtistAlbum">
              {artist && album ? (
                <div>
                  {artist}: {album}
                </div>
              ) : null}
            </div>
            {progressIndicator}
          </div>
        </div>
      </div>
    </div>
  )
}

export default function UploadTrackFile(props: Props) {
  const {
    isAdding,
    track,
    onClose,
    onDone,
    format,
    existingFormat,
    finalizeTrackUpload,
    trackUploader,
  } = props
  const [transcode, setTranscode] = useState(true)

  const [, dropRef] = useDrop<any>(
    () => ({
      accept: FILE,
      drop: (draggedItem, monitor) => {
        if (monitor.didDrop()) return
        if (!draggedItem?.items) return
        getFilesFromDraggedItem(draggedItem).then((files: File[]) => {
          if (files.length > 0) {
            handleUploadingFiles(props, files)
          }
        })
      },
    }),
    [props],
  )

  const hasUploaded = Boolean(trackUploader.uploadedToS3)

  const getOptimisedFormat = () => {
    const someFormat = format || existingFormat
    return getFileTypeByExt(someFormat) === AUDIO_FILE ? 'mp3' : 'mp4'
  }

  const shouldShowTranscodeCheckbox = () => {
    const { filename } = trackUploader
    if (!filename) return false
    const ext = getExtension(filename)
    const formatIsOptimised = !canFormatBeOptimised(ext)
    return hasUploaded && !formatIsOptimised
  }

  const handleDone = () => {
    finalizeTrackUpload(track.upload_policy_v2_url, transcode)
    onDone()
  }

  const successMessage = (
    <div>
      <div className="UploadTrackFile_Message UploadTrackFile_Row">
        {isAdding ? (
          <span>
            Nice work! You&#39;ve added a new format to{' '}
            <strong>&#34;{track.name}&#34;</strong>.
          </span>
        ) : (
          <span>
            Nice work! You&#39;ve replaced the <strong>.{format}</strong> of{' '}
            <strong>&#34;{track.name}&#34;</strong>.
          </span>
        )}
      </div>
    </div>
  )

  const uploadUI = (
    <div className="UploadTrackFile_Message UploadTrackFile_Row">
      <TempItem trackUploader={trackUploader} />
      <div ref={dndConnectRef(dropRef)}>
        <FileSelect
          className="UploadTrackFile_DropArea"
          onChange={(files: File[]) => handleUploadingFiles(props, files)}
        >
          Drag and drop file here, or click here to browse
        </FileSelect>
      </div>
    </div>
  )

  return (
    <Dialog.Root open onOpenChange={() => {}}>
      <Dialog.Content maxWidth="600px" aria-describedby={undefined}>
        <Theme>
          <Box>
            <Dialog.Title>
              {isAdding
                ? `Add format for ${track.name}`
                : `Replace file for ${track.name} (${format})`}
            </Dialog.Title>
            <Box mt="4">
              {hasUploaded ? successMessage : uploadUI}
              {shouldShowTranscodeCheckbox() ? (
                <label className="UploadTrackFile_TranscodeLabel UploadTrackFile_Row">
                  <input
                    className="UploadTrackFile_TranscodeCheckbox"
                    type="checkbox"
                    checked={transcode}
                    onChange={evt => setTranscode(evt.target.checked)}
                  />
                  Create a new {getOptimisedFormat()} copy of this track to
                  replace existing {getOptimisedFormat()}
                </label>
              ) : null}
              <Flex gap="3" mt="6" justify="end">
                {hasUploaded ? null : (
                  <Dialog.Close>
                    <Button
                      caption="Cancel"
                      appearance="subtle"
                      onClick={onClose}
                    />
                  </Dialog.Close>
                )}
                {hasUploaded ? (
                  <Button
                    caption="Done"
                    appearance="primary"
                    onClick={handleDone}
                  />
                ) : null}
              </Flex>
            </Box>
          </Box>
        </Theme>
      </Dialog.Content>
    </Dialog.Root>
  )
}
